import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import CloseIcon from '@material-ui/icons/Close';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { SESSION_EXPIRED_EVENT } from '../../services/auth-service';
import AristotleFrame from './AristotleFrame';

// Above the AppBar (1100) and Drawer (1200), below MUI modals (1300) and the
// global-search results (1400), so dialogs always win over the assistant.
const WIDGET_Z_INDEX = 1250;

const Launcher = styled.button`
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: #6AC5DA;
    color: #FFFFFF;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: ${WIDGET_Z_INDEX};
    &:hover {
        background: #55b6cc;
    }
`;

const Panel = styled.div`
    position: fixed;
    bottom: 88px;
    right: 20px;
    width: 420px;
    max-width: calc(100vw - 40px);
    height: min(640px, calc(100vh - 120px));
    border: 1px solid #d9e2e5;
    border-radius: 12px;
    overflow: hidden;
    background: #FFFFFF;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    z-index: ${WIDGET_Z_INDEX};
`;

/**
 * Floating Aristotle assistant: a fixed launcher icon on every authenticated
 * page that expands into an embedded Collaborate panel. Mounted ONCE in
 * MainProtectedLayout (which never unmounts across navigation), so the open
 * conversation survives moving between SIS pages; the iframe is created on
 * first open and only HIDDEN on collapse — never reloaded.
 *
 * Gated by aristotle_assistant_widget (the full-page /home experience has its
 * own flag, aristotle_assistant_home).
 */
const AristotleAssistant = () => {
    const enabled = useFeatureFlag(FEATURE_FLAGS.ARISTOTLE_WIDGET);
    const homeEnabled = useFeatureFlag(FEATURE_FLAGS.ARISTOTLE_HOME);
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [frameMounted, setFrameMounted] = useState(false);
    // Set when the backend reports NOT_CONFIGURED — hides the launcher for the
    // rest of the session instead of offering a dead button.
    const [unavailable, setUnavailable] = useState(false);
    // When the Home flag is on, /home hosts full-page Aristotle — a floating
    // Aristotle on top of it is noise. Hide (don't unmount) so the widget
    // conversation survives. With the Home flag off, /home is the legacy
    // Dashboard and the launcher behaves like on any other page.
    const onAssistantPage = homeEnabled && location.pathname === '/home';

    // Auth confirmed dead (401 path): drop the iframe with the session.
    useEffect(() => {
        const onSessionExpired = () => {
            setOpen(false);
            setFrameMounted(false);
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, []);

    if (!enabled || unavailable) return null;

    const toggle = () => {
        if (!frameMounted) setFrameMounted(true);
        setOpen((wasOpen) => !wasOpen);
    };

    const panelVisible = open && !onAssistantPage;

    return (
        <>
            <Launcher
                type="button"
                onClick={toggle}
                title="Ask Aristotle"
                aria-label={open ? 'Close Aristotle assistant' : 'Ask Aristotle'}
                aria-expanded={open}
                style={{ display: onAssistantPage ? 'none' : undefined }}
            >
                {open ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
            </Launcher>
            {frameMounted && (
                <Panel style={{ visibility: panelVisible ? 'visible' : 'hidden' }} aria-hidden={!panelVisible}>
                    <AristotleFrame
                        variant="widget"
                        visible={panelVisible}
                        onClose={() => setOpen(false)}
                        onUnavailable={() => setUnavailable(true)}
                    />
                </Panel>
            )}
        </>
    );
};

export default AristotleAssistant;
